// The visitor's help on a friend's farm (farm 5): a tap on a growing crop walks Natalka up and waters
// it after the progress bar; a tap on an animal offers Nakarm / Pogłaskaj. The server decides
// (POST /api/players/:host/farm/help); the counter and grey marks come from the visit view.
import { visitState } from '../state/visitState.js';
import { gameState } from '../state/gameState.js';
import { social } from '../api/social.js';
import { audioEvents } from '../audio/audioEvents.js';
import { animalSoundEvent } from '../audio/eventMap.js';
import { showToast } from '../ui/Toast.js';
import { showActionsModal } from '../ui/Modal.js';
import { iconText } from '../ui/iconText.js';
import { cellCentre } from './iso.mjs';
import { areaCentre, areaKey } from './footprint.mjs';
import { waterDrop } from './farmFxArt.js';
import { realtime } from '../api/realtime.js';
import { progressThrottle } from './farmFx.mjs';
import { cellHelp, areaHelp, areaPreview, animalHelpItems, helpCounter, helpFloat, REJECT_TOAST, pendingGuard, newMarks, levelUps } from './visitHelp.mjs';
import { serverNow } from '../state/serverClock.mjs';

const WORK_MS = 600;                                // the owner's hand-job time
const VERTICAL_FACTOR = 0.6, ARRIVE_PX = 40;       // WalkController scales dy by 0.6 (as FarmScene pre-divides)

export function attachVisitHelp(scene, { counterPos = { x: 640, y: 64 } } = {}) {
  const guard = pendingGuard();
  // A small wooden pill under the level badge, like the farm's other HUD pieces (FarmHud's x2 XP hour).
  const { x: px, y: py } = counterPos, PW = 170, PH = 32;
  const pill = scene.add.graphics().setScrollFactor(0).setDepth(1e6 - 1);
  pill.fillStyle(0x000000, 0.25).fillRoundedRect(px - PW / 2 + 2, py - PH / 2 + 3, PW, PH, 14);
  pill.fillStyle(0x6e4220, 1).fillRoundedRect(px - PW / 2, py - PH / 2, PW, PH, 14);
  pill.fillStyle(0xb57a43, 1).fillRoundedRect(px - PW / 2 + 3, py - PH / 2 + 3, PW - 6, PH - 6, 11);
  const counter = iconText(scene, px, py, '', { fontFamily: 'system-ui', fontSize: '17px', color: '#fff8e8',
    fontStyle: 'bold', stroke: '#5a3414', strokeThickness: 4 }).setOrigin(0.5).setScrollFactor(0).setDepth(1e6);
  // A water drop over each crop helped today — popped in once, held a moment, faded out (owner
  // 2026-09-30: a plain dot didn't read, and it should disappear after a while).
  const shown = new Set();
  const drop = (cx, cy) => waterDrop(scene, cx, cy);
  const drawMarks = () => {
    for (const { x, y } of newMarks(visitState.view, shown)) { const c = cellCentre(x, y, scene.world.size); drop(c.x, c.y); }
  };
  const refreshCounter = () => {
    counter.setText(helpCounter(Math.max(0, (visitState.view?.help_left ?? 0) - guard.size)));
    drawMarks();
  };
  refreshCounter();
  let walk = null;                                    // { to: {x,y} local, then }

  const send = async (key, op, at, species = null) => {
    try {
      const out = await social.help(visitState.hostId, op);
      const water = op.op === 'water' || op.op === 'water_area';
      audioEvents.emit(water ? 'water' : animalSoundEvent(op.op === 'feed' ? 'feed_animal' : 'pet_animal', species));   // farm 6
      visitState.view.help_left = out.helps_left;
      if (!water) visitState.view.helped_today.animals.push(op.id);
      for (const w of out.watered ?? []) visitState.view.helped_today.cells.push(`${w.x},${w.y}`);
      scene.world.refresh(serverNow());
      floatAt(at, helpFloat(out));                    // what it did: "−1 h 20 min" / "Gotowe!", and the reward
      if (op.op === 'pet') scene.world.animalLayer.hearts(op.id);   // the same hearts as the owner's petting
      // …and the host (and anyone else on the farm) sees it too: the drop / hearts, the sound, the reward —
      // for a Konewka pour, at each field it watered
      if (water) {
        for (const w of out.watered ?? []) {
          const c = cellCentre(w.x, w.y, scene.world.size);
          realtime.farmFx({ kind: 'water', animal: null, x: c.x, y: c.y, money: 5, xp: 2 });
        }
      } else realtime.farmFx({ kind: op.op, animal: op.id, x: at.x, y: at.y, money: 5, xp: 2 });
      scene.popups?.show(levelUps(out.events));      // helping can level the helper up: the farm's level-up card
      gameState.refresh().catch(() => {});           // the visitor's own coins / XP / level
    } catch (e) {
      showToast(scene, REJECT_TOAST[e?.error_code] ?? e?.message ?? 'Nie udało się');
    } finally { guard.done(key); refreshCounter(); scene.events.emit('helped'); }   // the Konewka's hover is redrawn
  };

  const floatAt = (at, label) => {
    const t = iconText(scene, at.x, at.y - 48, label, { fontFamily: 'system-ui', fontSize: '18px', color: '#ffe066',
      fontStyle: 'bold', stroke: '#000', strokeThickness: 3 }).setOrigin(0.5).setDepth(1e9);
    scene.world.objects.add(t);
    scene.tweens.add({ targets: t, y: t.y - 40, alpha: 0, duration: 1600, ease: 'Quad.In', onComplete: () => t.destroy() });
  };

  // Walk up through the scene's WalkController (its autoVector hook, as FarmScene's hand jobs): the
  // walk animates, is broadcast to the other viewers, and the keys / joystick can take over. Then the
  // progress bar, then the request.
  const walkThenWork = (target, action, then) => { walk = { to: target, action, then }; };

  const autoVector = () => {
    if (!walk || !scene.natalka) return null;
    const n = scene.natalka, dx = walk.to.x - n.x, dy = walk.to.y - n.feetY;
    if (Math.hypot(dx, dy) > ARRIVE_PX) return { dx, dy: dy / VERTICAL_FACTOR };
    const { then, action, to } = walk; walk = null;
    guard.arrived();
    const bar = scene.add.rectangle(n.x - 30, n.y - 90, 0, 8, 0x7ed957).setOrigin(0, 0.5).setDepth(1e9);
    scene.world.objects.add(bar);
    const fx = { tool: 'hand', action, x: to.x, y: to.y }, ok = progressThrottle(250);
    realtime.farmFx({ kind: 'job_start', ...fx });
    scene.tweens.add({ targets: bar, width: 60, duration: WORK_MS,
      onUpdate: (tw) => { if (ok(tw.progress, serverNow())) realtime.farmFx({ kind: 'job_progress', ...fx, p: tw.progress }); },
      onComplete: () => { bar.destroy(); realtime.farmFx({ kind: 'job_end', ...fx }); then(); } });
    return null;
  };

  return {
    tapCell(cell) {
      const r = cellHelp(visitState.view, cell, serverNow());
      if (!r.ok) { showToast(scene, r.toast, 1500); return; }
      const key = `cell:${cell.x},${cell.y}`;
      if (!guard.start(key, visitState.view.help_left)) return;
      refreshCounter();
      const at = cellCentre(cell.x, cell.y, scene.world.size);
      walkThenWork(at, r.op.op, () => send(key, r.op, at));
    },
    // What a Konewka click on this block would water (the hover's fields and hint).
    previewArea(cells) { return areaPreview(visitState.view, cells, serverNow(), guard.size); },
    // The Konewka (owner 2026-10-27): one walk and one bar for the whole 2×2 block, one request.
    tapArea(cells) {
      const r = areaHelp(visitState.view, cells, serverNow());
      if (!r.ok) { showToast(scene, r.toast, 1500); return; }
      const key = `area:${areaKey(cells)}`;
      if (!guard.start(key, visitState.view.help_left, r.n)) return;
      refreshCounter();
      const at = areaCentre(cells, scene.world.size);
      walkThenWork(at, 'water', () => send(key, r.op, at));
    },
    async tapAnimal(a) {
      const items = animalHelpItems(visitState.view, a);
      if (!items.length) { showToast(scene, REJECT_TOAST.HELP_ALREADY, 1500); return; }
      if ((visitState.view.help_left ?? 0) - guard.size <= 0) { showToast(scene, REJECT_TOAST.HELP_LIMIT, 1500); return; }
      // showActionsModal has no disabled buttons: an unavailable one becomes a line in the body
      const usable = items.filter((i) => i.enabled), body = items.filter((i) => !i.enabled).map((i) => i.label).join('\n');
      const pick = await showActionsModal(scene, { title: a.name ?? gameState.raw?.catalog?.farm?.animals?.[a.type]?.name_pl ?? '', body, actions: usable.map((i) => ({ label: i.label, fn: () => i.op })) });
      if (pick !== 'feed' && pick !== 'pet') return;
      const key = `animal:${a.id}`;
      if (!guard.start(key, visitState.view.help_left)) return;
      refreshCounter();
      const p = scene.world.animalLayer.cellOf(a.id) ?? { x: 0, y: 0 };
      const at = cellCentre(p.x, p.y, scene.world.size);
      walkThenWork(at, pick, () => send(key, { op: pick, id: a.id }, at, a.type));
    },
    autoVector,
    update() {},
    refreshCounter,
    destroy() { counter.destroy(); pill.destroy(); },
  };
}
